/**
 * Shift calories toward training days, keeping the WEEKLY total unchanged.
 *
 * Why not "eat back what you burn": the adaptive expenditure already contains
 * your training (it's learned from intake and the scale over weeks that
 * included it), so adding a workout's burn on top counts it twice. Shifting
 * moves calories between days instead: training days get more, rest days pay
 * for it, and the week comes out the same.
 *
 *   bonus(day) = min(400, ½ × that day's estimated burn)     (burn ≥ 100 kcal)
 *   rest cut   = average weekly bonus ÷ rest days per week   (≤ 250 kcal)
 *
 * Half the burn, because lifting estimates are ±40%; and both sides are sized
 * from the last 28 days, so the cut funds the bonuses you actually earn. Every
 * day starts as a rest day — a workout that syncs in from Lift turns it into a
 * training day. Off when there's too little training to be worth it (under
 * ~1 session a week) or too much (almost no rest days to take from).
 */

import { addDays, dayRange } from './days.ts'

export const BURN_SHARE = 0.5
export const MAX_BONUS = 400
export const MAX_REST_CUT = 250
export const MIN_TRAINING_BURN = 100
export const WINDOW = 28

const r5 = (v: number) => Math.round(v / 5) * 5

export type Shift = {
  active: boolean
  reason: string | null
  restCut: number
  trainingDaysPerWeek: number
  bonusFor: (day: string) => number
}

export function bonusForBurn(burn: number): number {
  return burn >= MIN_TRAINING_BURN ? r5(Math.min(MAX_BONUS, BURN_SHARE * burn)) : 0
}

export function trainingShift(burns: Map<string, number>, today: string, enabled: boolean): Shift {
  const bonusFor = (d: string) => bonusForBurn(burns.get(d) ?? 0)
  const off = (reason: string | null): Shift =>
    ({ active: false, reason, restCut: 0, trainingDaysPerWeek: 0, bonusFor: () => 0 })
  if (!enabled) return off('Turned off')

  const window = dayRange(addDays(today, -WINDOW), addDays(today, -1))
  const training = window.filter((d) => (burns.get(d) ?? 0) >= MIN_TRAINING_BURN)
  const perWeek = (training.length * 7) / WINDOW
  if (training.length < 4) return off('Needs about one Lift workout a week to shift toward')
  const restPerWeek = 7 - perWeek
  if (restPerWeek < 0.75) return off('You train almost every day — nothing to shift from')

  const weeklyBonus = (training.reduce((a, d) => a + bonusFor(d), 0) * 7) / WINDOW
  const wanted = weeklyBonus / restPerWeek
  const restCut = r5(Math.min(MAX_REST_CUT, wanted))
  // When the cut is capped, the rest days can't fund full bonuses — shrink the
  // bonuses to what they CAN fund, or the week comes out over. Real case: 4.5
  // sessions a week leaves 2.5 rest days, and the week ran ~250 kcal high.
  const scale = wanted > 0 ? Math.min(1, (restCut * restPerWeek) / weeklyBonus) : 1
  const scaled = (d: string) => r5(bonusFor(d) * scale)
  return { active: true, reason: null, restCut, trainingDaysPerWeek: Math.round(perWeek * 10) / 10, bonusFor: scaled }
}

export type TargetLike = { kcal: number; protein_g: number; carbs_g: number; fat_g: number; fibre_g?: number | null }

export type EffectiveTarget = TargetLike & {
  baseKcal: number
  delta: number
  kind: 'training' | 'rest' | 'none'
}

/** A day's target after the shift. The change is carried by carbs (protein and
 *  fat targets are about the body, not the day); if carbs would go negative the
 *  remainder comes out of fat. */
export function effectiveTarget(base: TargetLike, shift: Shift, day: string): EffectiveTarget {
  if (!shift.active) return { ...base, baseKcal: base.kcal, delta: 0, kind: 'none' }
  const bonus = shift.bonusFor(day)
  // Training days get the bonus; ONLY rest days pay for it. (Taking the cut
  // on training days too left every week ~450 kcal short — the test caught it.)
  const delta = bonus > 0 ? bonus : -shift.restCut
  let carbs = base.carbs_g + delta / 4
  let fat = base.fat_g
  if (carbs < 0) { fat = Math.max(0, fat + (carbs * 4) / 9); carbs = 0 }
  return {
    ...base, kcal: base.kcal + delta, carbs_g: Math.round(carbs), fat_g: Math.round(fat),
    baseKcal: base.kcal, delta, kind: bonus > 0 ? 'training' : 'rest',
  }
}
